import React from 'react'
import {Box, Divider, Flex, Spinner, Text} from '../../utils/chakraMonomorphic'

import {PageHeader} from '../../components/PageHeader/PageHeader'
import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'
import {usePersonTaggingDiagnostics} from '../../hooks/usePersonTaggingDiagnostics'
import {useRequiredAuth} from '../../stores/AuthStore'

import copy from './copy.json'
import {resolveCoverageState} from './coverageState'
import {
  CloseCallsTable,
  CoverageNote,
  LookupForm,
  LookupSubmission,
  SeasonTagStats,
  TrainingPhotoPanel,
} from './subcomps'

/**
 * CampMinder-employee-only per-camper facial-recognition diagnostics.
 *
 * Answers, for one camper in one camp and season: how many of their photos are
 * tagged and by what (recognition vs. a human), when their training photo was
 * enrolled, and — the reason the page exists — which photos a person tagged
 * manually that facial recognition either already matched or very nearly did.
 *
 * Visibility is gated in three places, only the last of which is a security
 * boundary:
 *   - `useIsCampMinderEmployee()` and the ConfigCat flag hide the nav item and
 *     the route (see SideMenuTemplate / Router). Both are UX affordances.
 *   - The faces service enforces the authoritative `campminderstaff` check plus
 *     per-client scope, returning 403. That is the real gate.
 */
export const FrDiagnosticsView: React.FC<React.PropsWithChildren> = () => {
  const [lookup, setLookup] = React.useState<LookupSubmission | null>(null)
  // Undefined until the operator picks one, so the first request lets the
  // SERVER resolve "most recent season with data" — a camp's seasonID is not
  // guaranteed to track the calendar year, so the client must not guess.
  const [season, setSeason] = React.useState<number | undefined>(undefined)

  const {client} = useRequiredAuth()

  // The album route is scoped to the CURRENTLY-SELECTED client: it authorizes
  // and fetches with the session's client id, not the one typed into the lookup
  // form. So an album link is only meaningful when those agree. The albums
  // service fails closed on a mismatch (no data leak) — but album ids are only
  // unique per client, so a collision would render a DIFFERENT camp's album
  // successfully, with the highlight landing on nothing or on an unrelated
  // photo. On a diagnostics page that invites a confident wrong conclusion
  // about which camper was tagged, so withhold the link instead.
  const canLinkAlbums = lookup !== null && Number(client.ID) === lookup.clientId

  const {status, data, nameMap, errorMessage} = usePersonTaggingDiagnostics({
    personId: lookup?.personId ?? null,
    clientId: lookup?.clientId ?? null,
    season,
    enabled: lookup !== null,
  })

  const handleSubmit = React.useCallback((submission: LookupSubmission) => {
    // Clear any previously-picked season: it belongs to the previous camper
    // and may not exist for the new one.
    setSeason(undefined)
    setLookup(submission)
  }, [])

  const pageHeader = (
    <PageHeader
      title={copy.title}
      logContext='FrDiagnostics'
      description={copy.description}
    />
  )

  const renderResults = (): React.ReactElement | null => {
    if (status === 'idle') {
      return (
        <Text fontSize='sm' color='gray.700' data-testid='fr-diagnostics-idle'>
          {copy.states.idle}
        </Text>
      )
    }

    if (status === 'pending') {
      return (
        <Flex alignItems='center' gap={3} data-testid='fr-diagnostics-loading'>
          <Spinner size='sm' />
          <Text fontSize='sm'>{copy.states.loading}</Text>
        </Flex>
      )
    }

    if (status === 'rejected') {
      // Covers the 403 "not authorized for that camp" case, which the api
      // layer rewrites into page-specific copy.
      return (
        <Text color='red.600' fontSize='sm' data-testid='fr-diagnostics-error'>
          {errorMessage}
        </Text>
      )
    }

    if (!data || !lookup) return null

    const coverageState = resolveCoverageState(data.closeCalls.coverage)

    return (
      <Box>
        {data.seasons.length === 0 ? (
          <Text
            fontSize='sm'
            color='gray.700'
            mb={8}
            data-testid='fr-diagnostics-no-tags'>
            {copy.states.noTags}
          </Text>
        ) : (
          <SeasonTagStats
            seasons={data.seasons}
            selectedSeason={data.season}
            onSelectSeason={setSeason}
            photos={data.photos}
            clientId={lookup.clientId}
            canLinkAlbums={canLinkAlbums}
          />
        )}

        <Divider mb={6} />

        <TrainingPhotoPanel
          training={data.training}
          personId={lookup.personId}
          clientId={lookup.clientId}
        />

        <Divider mb={6} />

        <Box>
          <Text fontSize='1.05rem' fontWeight='bold'>
            {copy.closeCalls.heading}
          </Text>
          <Text fontSize='sm' color='gray.700' mb={4}>
            {copy.closeCalls.description}
          </Text>

          {/* What the recognition data can support, stated ONCE. Coverage is a
              property of the data, not of either table, so rendering it per
              table duplicated the whole paragraph on one screen. Shown whether
              or not the tables have rows — a list built from partial data is
              misleading either way. */}
          <CoverageNote state={coverageState} />

          <CloseCallsTable
            heading={copy.closeCalls.acceptedHeading}
            description={copy.closeCalls.acceptedDescription}
            emptyMessage={copy.closeCalls.acceptedEmpty}
            calls={data.closeCalls.accepted}
            nameMap={nameMap}
            testId='close-calls-accepted'
            clientId={lookup.clientId}
            coverageState={coverageState}
            inconclusiveMessage={copy.closeCalls.acceptedInconclusive}
            canLinkAlbums={canLinkAlbums}
          />

          <CloseCallsTable
            heading={copy.closeCalls.nearMissHeading}
            description={copy.closeCalls.nearMissDescription}
            emptyMessage={copy.closeCalls.nearMissEmpty}
            calls={data.closeCalls.nearMiss}
            nameMap={nameMap}
            testId='close-calls-near-miss'
            clientId={lookup.clientId}
            coverageState={coverageState}
            inconclusiveMessage={copy.closeCalls.nearMissInconclusive}
            canLinkAlbums={canLinkAlbums}
          />
        </Box>
      </Box>
    )
  }

  return (
    <PageTemplate
      header={pageHeader}
      width={['100%']}
      maxWidth={['1030px']}
      borderX={['0px']}>
      <Flex direction='column' px='.75em'>
        <Box borderTop='1px' mt={3} mb={6} />
        <LookupForm onSubmit={handleSubmit} isLoading={status === 'pending'} />
        {renderResults()}
      </Flex>
    </PageTemplate>
  )
}
